import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Scroll-triggered visibility hook using Intersection Observer
 */
export function useScrollReveal(options = {}) {
  const { threshold = 0.12, rootMargin = "0px 0px -60px 0px", once = true } = options;
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // SSR guard
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.unobserve(node);
  }, [threshold, rootMargin, once]);

  return [ref, isVisible];
}

/**
 * Animated counter that counts up from 0 to `end`
 */
export function useCountUp(end, duration = 2000, isActive = false) {
  // Start at the real value (not 0) so server-rendered HTML shows the actual
  // stat before JS runs; the scroll-triggered animation still resets to 0
  // and counts back up for visitors who see it happen.
  const [count, setCount] = useState(end);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    setCount(0);

    let start = null;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * end));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [end, duration, isActive]);

  return count;
}
